import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { clearContentCache } from "@/lib/useContent";
import { formatApiError } from "@/context/AuthContext";
import {
    Save, RotateCcw, Plus, Trash2, Edit3, Check, X,
    FileText, Film, BookOpen, MapPin, Inbox, Image as ImageIcon, Upload, Navigation, UserCheck, Radio, CalendarClock, Ban, MessageSquareQuote, Star, Mic, Play,
} from "lucide-react";
import { Field } from "../shared.jsx";

const STATUS_LABEL = {
    pending: { label: "Pending", tone: "text-yellow-300 border-yellow-300/40" },
    confirmed: { label: "Confirmed", tone: "text-emerald-300 border-emerald-300/40" },
    declined: { label: "Declined", tone: "text-red-300 border-red-300/40" },
    cancelled: { label: "Cancelled", tone: "text-white/40 border-white/20" },
};

export default function BookingsManager() {
    const [bookings, setBookings] = useState([]);
    const [blackouts, setBlackouts] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [newBlackout, setNewBlackout] = useState({ start_date: "", end_date: "", reason: "" });

    const load = async () => {
        const [b, bo] = await Promise.all([
            api.get("/admin/bookings"),
            api.get("/admin/blackouts"),
        ]);
        setBookings(b.data);
        setBlackouts(bo.data);
    };
    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/bookings/${id}`, { status });
            toast.success(`Booking ${status}.`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const removeBooking = async (id) => {
        if (!window.confirm("Delete this booking?")) return;
        try {
            await api.delete(`/admin/bookings/${id}`);
            toast.success("Deleted.");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const addBlackout = async (e) => {
        e.preventDefault();
        if (!newBlackout.start_date || !newBlackout.end_date) {
            toast.error("Both start and end dates are required.");
            return;
        }
        try {
            await api.post("/admin/blackouts", newBlackout);
            toast.success("Blackout added.");
            setNewBlackout({ start_date: "", end_date: "", reason: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const removeBlackout = async (id) => {
        if (!window.confirm("Remove this blackout?")) return;
        try {
            await api.delete(`/admin/blackouts/${id}`);
            toast.success("Removed.");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const filtered = statusFilter === "all" ? bookings : bookings.filter((b) => b.status === statusFilter);

    const counts = bookings.reduce((acc, b) => { acc[b.status] = (acc[b.status] || 0) + 1; return acc; }, {});

    return (
        <div data-testid="bookings-manager" className="space-y-10">
            {/* Summary + filter */}
            <div>
                <div className="overline mb-3">Client shoot requests</div>
                <div className="flex flex-wrap items-center gap-2 mb-6">
                    <button
                        onClick={() => setStatusFilter("all")}
                        className={`text-xs uppercase tracking-[0.22em] px-3 py-2 border ${statusFilter === "all" ? "border-[var(--sb-brand)] text-[var(--sb-text)]" : "border-[var(--sb-border)] text-[var(--sb-text-dim)] hover:text-[var(--sb-text)]"}`}
                        data-testid="bookings-filter-all"
                    >
                        All · {bookings.length}
                    </button>
                    {Object.keys(STATUS_LABEL).map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            className={`text-xs uppercase tracking-[0.22em] px-3 py-2 border ${statusFilter === s ? "border-[var(--sb-brand)] text-[var(--sb-text)]" : "border-[var(--sb-border)] text-[var(--sb-text-dim)] hover:text-[var(--sb-text)]"}`}
                            data-testid={`bookings-filter-${s}`}
                        >
                            {STATUS_LABEL[s].label} · {counts[s] || 0}
                        </button>
                    ))}
                </div>

                {filtered.length === 0 ? (
                    <div className="card p-10 text-center text-[var(--sb-text-dim)]">
                        No bookings in this view yet.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filtered.map((b) => (
                            <div key={b.id} className="card p-5 grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid={`admin-booking-${b.id}`}>
                                <div className="lg:col-span-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className={`text-[10px] tracking-[0.22em] uppercase border px-2 py-1 ${STATUS_LABEL[b.status]?.tone || "text-white/50 border-white/20"}`}>
                                            {STATUS_LABEL[b.status]?.label || b.status}
                                        </span>
                                        <span className="text-xs text-[var(--sb-text-muted)]">{b.shoot_type}</span>
                                    </div>
                                    <div className="font-serif text-lg">{b.client_name}</div>
                                    <div className="text-sm text-[var(--sb-text-dim)] truncate">
                                        <a href={`mailto:${b.email}`} className="hover:text-[var(--sb-brand)]">{b.email}</a>
                                        {b.phone && <> · <a href={`tel:${b.phone}`} className="hover:text-[var(--sb-brand)]">{b.phone}</a></>}
                                    </div>
                                </div>
                                <div className="lg:col-span-4 text-sm">
                                    <div className="overline">Date · time</div>
                                    <div className="font-serif text-lg">{b.preferred_date}</div>
                                    <div className="text-[var(--sb-text-dim)]">{b.time_slot} {b.alternate_date && <span className="text-xs">(alt: {b.alternate_date})</span>}</div>
                                    {(b.lat != null && b.lng != null) && (
                                        <a
                                            href={`https://www.google.com/maps?q=${b.lat},${b.lng}`}
                                            target="_blank" rel="noreferrer"
                                            className="mt-1 text-xs text-[var(--sb-brand)] hover:text-[var(--sb-text)] inline-flex items-center gap-1"
                                        >
                                            {b.location_name || "View pin"} ({b.lat.toFixed(3)}, {b.lng.toFixed(3)}) ↗
                                        </a>
                                    )}
                                    {b.notes && <p className="text-xs text-[var(--sb-text-muted)] mt-2 line-clamp-3">{b.notes}</p>}
                                </div>
                                <div className="lg:col-span-4 flex flex-wrap gap-2 lg:justify-end items-start">
                                    {b.status !== "confirmed" && (
                                        <button onClick={() => setStatus(b.id, "confirmed")} className="text-xs px-3 py-2 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid={`booking-confirm-${b.id}`}>
                                            Confirm
                                        </button>
                                    )}
                                    {b.status !== "declined" && (
                                        <button onClick={() => setStatus(b.id, "declined")} className="text-xs px-3 py-2 border border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`booking-decline-${b.id}`}>
                                            Decline
                                        </button>
                                    )}
                                    {b.status !== "pending" && (
                                        <button onClick={() => setStatus(b.id, "pending")} className="text-xs px-3 py-2 border border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/10">
                                            Reopen
                                        </button>
                                    )}
                                    <button onClick={() => removeBooking(b.id)} className="text-xs px-3 py-2 border border-[var(--sb-border)] text-[var(--sb-text-muted)] hover:text-red-400 hover:border-red-400/40" data-testid={`booking-delete-${b.id}`}>
                                        <Trash2 size={12} className="inline" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Blackout dates */}
            <div className="border-t border-[var(--sb-border)] pt-8">
                <div className="flex items-center gap-3 mb-3">
                    <Ban size={16} className="text-[var(--sb-brand)]" />
                    <div className="overline">Blackout dates</div>
                </div>
                <p className="text-sm text-[var(--sb-text-dim)] max-w-xl mb-5">
                    Block off ranges when you can&rsquo;t fly (NC500 trip, family, weather windows). These automatically hide from the public booking calendar.
                </p>

                <form onSubmit={addBlackout} className="card p-5 grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-5" data-testid="blackout-form">
                    <Field label="Start">
                        <input type="date" className="input-line" value={newBlackout.start_date} onChange={(e) => setNewBlackout({...newBlackout, start_date: e.target.value})} data-testid="blackout-start" />
                    </Field>
                    <Field label="End">
                        <input type="date" className="input-line" value={newBlackout.end_date} onChange={(e) => setNewBlackout({...newBlackout, end_date: e.target.value})} data-testid="blackout-end" />
                    </Field>
                    <Field label="Reason (optional)">
                        <input className="input-line" value={newBlackout.reason} onChange={(e) => setNewBlackout({...newBlackout, reason: e.target.value})} placeholder="NC500 recon trip" data-testid="blackout-reason" />
                    </Field>
                    <button type="submit" className="btn-primary text-sm" style={{padding: "0.55rem 1rem"}} data-testid="blackout-add">
                        <Plus size={14} /> Block dates
                    </button>
                </form>

                {blackouts.length === 0 ? (
                    <div className="text-sm text-[var(--sb-text-muted)]">No blackouts set.</div>
                ) : (
                    <div className="space-y-2">
                        {blackouts.map((bo) => (
                            <div key={bo.id} className="card p-4 flex items-center justify-between gap-3" data-testid={`blackout-${bo.id}`}>
                                <div>
                                    <div className="font-serif text-base">{bo.start_date} → {bo.end_date}</div>
                                    {bo.reason && <div className="text-xs text-[var(--sb-text-dim)]">{bo.reason}</div>}
                                </div>
                                <button onClick={() => removeBlackout(bo.id)} className="text-[var(--sb-text-muted)] hover:text-red-400 p-2" data-testid={`blackout-delete-${bo.id}`}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Testimonials moderation                                                    */
